import { useCallback, useContext, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { FormContextAMR } from "../form-context";
import { searchInterventions } from "../../../redux/modules/intervention/actions";
import {
  isResolvedIntervention,
} from "../../patient/Patient-Menu/Interventions/data";
import medsCheckLogo from "../images/meds-check-logo.png";
import ontarioLeftLogo from "../images/ontario-logo-new.png";
import ontarioPharmaLogo from "../images/ontario-pharma-logo.png";
import {
  drugTypes,
  getFullLabelByValue,
  reasonOptions,
} from "../../patient/clinical-services/service-steps/clinical-assessment/data";
import { getPharmacyInfo } from "../common/getPharmacyInfo";
import { GetDropDownValue } from "../components/data";
import {
  AdherenceTypes,
  getGeneralTimeFrequency,
} from "../../patient/clinical-services/service-steps/medication/data";
import {
  getInterventionLinkedMrServiceId,
  isMarkedForPersonalMedicationReview,
  normalizeInterventionsPayload,
} from "../../../helpers/medication-review-pmr-link";

const COMPLETED_STATUSES = ["completed", "complete", "done", "cancelled"];

const isResolvedStatus = (status) => {
  return COMPLETED_STATUSES.includes(
    String(status || "").trim().toLowerCase()
  );
};

/** Empty list reused for every unset medication source so effect/memo deps stay reference-stable. */
const EMPTY_LIST = [];

/** `yyyy/mm/dd` for the form; blank for missing or unparseable values (never throws on a bad date). */
const formatFormDate = (value) => {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toISOString().split("T")[0].replace(/-/g, "/");
};

/**
 * Caregiver / care provider are stored as collections on the patient profile
 * (see referrals and PrimaryHealthcareProvider), and may be absent entirely.
 */
const firstProfileEntry = (value) => {
  if (Array.isArray(value)) return value[0] ?? {};
  return value ?? {};
};

const primaryCareProvider = (careProvider) => {
  if (Array.isArray(careProvider)) {
    return careProvider.find((provider) => provider?.primaryProvider) || careProvider[0] || {};
  }
  return careProvider ?? {};
};

/** When viewing a specific AMR service PMR, only show interventions scoped to that service (or legacy: created on that service). */
const interventionAppliesToPmrService = (intervention, pmrServiceId) => {
  if (pmrServiceId == null || pmrServiceId === "") return true;
  const linkId = getInterventionLinkedMrServiceId(intervention);
  if (linkId != null && linkId !== "") {
    return String(linkId) === String(pmrServiceId);
  }
  const createdOn = intervention?.clinicalServiceId;
  if (createdOn != null && createdOn !== "") {
    return String(createdOn) === String(pmrServiceId);
  }
  return true;
};

const findInterventionsForMedicationRow = (med, interventionsList, pmrServiceId) => {
  if (!med || !Array.isArray(interventionsList)) return [];
  // Obj IDs from the API vary in formatting — normalize them like getAmrMedicationRowForIntervention.
  const normId = (x) => String(x).toLowerCase().replace(/[{}]/g, "");
  const idSet = new Set(
    [med.id, med.medicationId].filter(Boolean).map(normId)
  );
  const interventionMedicationId = (inv) =>
    inv?.medicationId ??
    inv?.interventionAssessment?.interventionAssessmentForm?.interventionDetails
      ?.medicationId ??
    null;
  return interventionsList.filter((inv) => {
    if (!interventionAppliesToPmrService(inv, pmrServiceId)) return false;
    if (!isMarkedForPersonalMedicationReview(inv)) return false;
    const invMedId = interventionMedicationId(inv);
    if (invMedId != null && idSet.has(normId(invMedId))) {
      return true;
    }
    if (med?.intervention?.id && normId(inv?.id) === normId(med.intervention.id)) return true;
    if (med?.interventionId && normId(inv?.id) === normId(med.interventionId)) return true;
    return false;
  });
};

const isEmbeddedInterventionPending = (med) => {
  const inv = med?.intervention;
  if (!inv) return false;
  if (Array.isArray(inv)) {
    return inv.some(
      (x) =>
        isMarkedForPersonalMedicationReview(x) &&
        x &&
        !isResolvedStatus(x?.status) &&
        !isResolvedIntervention(x)
    );
  }
  if (!isMarkedForPersonalMedicationReview(inv)) return false;
  if (typeof inv === "object" && Object.keys(inv).length === 0) return false;
  if (isResolvedStatus(inv.status)) return false;
  if (isResolvedIntervention(inv)) return false;
  return true;
};

/** Row's embedded intervention was marked for the PMR — resolved or not. */
const rowHasEmbeddedPmrIntervention = (med) => {
  const inv = med?.intervention;
  if (!inv) return false;
  if (Array.isArray(inv)) return inv.some((x) => isMarkedForPersonalMedicationReview(x));
  return isMarkedForPersonalMedicationReview(inv);
};

/** Row is tied to a PMR-marked intervention (store match or embedded), resolved or not. */
const rowHasAnyPmrIntervention = (med, interventionsList, pmrServiceId) => {
  if (!med) return false;
  if (findInterventionsForMedicationRow(med, interventionsList, pmrServiceId).length > 0) {
    return true;
  }
  return rowHasEmbeddedPmrIntervention(med);
};

const isInterventionPendingForMedicationRow = (med, interventionsList, pmrServiceId) => {
  if (!med) return false;
  const matches = findInterventionsForMedicationRow(med, interventionsList, pmrServiceId);
  const hasPendingFromStore =
    matches.length > 0 && matches.some((inv) => !isResolvedStatus(inv.status));
  if (hasPendingFromStore) return true;
  return isEmbeddedInterventionPending(med);
};

/** Keys for matching profile medications to assessment rows (id vs medicationId, string vs number). */
const idKeysForMed = (m) => {
  const keys = new Set();
  if (m?.id != null) {
    keys.add(m.id);
    keys.add(String(m.id));
  }
  if (m?.medicationId != null) {
    keys.add(m.medicationId);
    keys.add(String(m.medicationId));
  }
  return keys;
};

const buildPatientMedicationLookupMap = (patientMeds) => {
  const map = new Map();
  for (const pm of patientMeds) {
    for (const k of idKeysForMed(pm)) {
      if (!map.has(k)) map.set(k, pm);
    }
  }
  return map;
};

const findPatientMedicationForRow = (assessmentMed, lookupMap, patientMeds) => {
  if (!assessmentMed) return null;
  for (const k of idKeysForMed(assessmentMed)) {
    const found = lookupMap.get(k);
    if (found) return found;
  }
  const aid = assessmentMed.medicationId ?? assessmentMed.id;
  if (aid == null) return null;
  for (const pm of patientMeds) {
    if (pm.id === aid || pm.medicationId === aid) return pm;
    if (String(pm.id) === String(aid) || String(pm.medicationId) === String(aid)) return pm;
  }
  return null;
};

const cardRowMatchesPatientMed = (cardRow, pm) =>
  cardRow?.id === pm.id ||
  cardRow?.id === pm.medicationId ||
  String(cardRow?.id) === String(pm.id) ||
  String(cardRow?.id) === String(pm.medicationId);

const formatHowITakeIt = (row) => {
  const freqLabel = row.frequency ? getFullLabelByValue(row.frequency) : "";
  const parts = [
    row.route,
    row.dispenseQuantity != null && row.dispenseQuantity !== "" ? String(row.dispenseQuantity) : "",
    freqLabel,
    getGeneralTimeFrequency(row),
  ];
  return parts.filter((v) => v != null && v !== "").join(", ");
};

export const PersonalMedicationRecordForm = () => {
  const dispatch = useDispatch();
  const { id: patientIdFromRoute } = useParams();
  const {
    patient,
    medications,
    startDateTime,
    endDateTime,
    patientMedications,
    documentation,
    serviceId: pmrClinicalServiceId,
    hasActiveInterventions: hasActiveInterventionsFromContext,
    pmrIsFinal,
  } = useContext(FormContextAMR);

  const patientId = patient?.id ?? patientIdFromRoute;
  const interventionsFromStore = useSelector(
    (state) => state.intervention?.interventions ?? []
  );
  // The `/search` list payload frequently omits PMR-link fields (see intervention actions).
  // The fully-detailed intervention being viewed lives on `currentIntervention`; fold it in
  // so the unchanged row-match logic below has the `addToPersonalMedicationReview` flag.
  const currentInterventionFromStore = useSelector(
    (state) => state.intervention?.currentIntervention
  );
  const clinicalServicesSmall = useSelector(
    (state) => state.clinicalService?.clinicalServicesSmall
  );
  const interventionsList = useMemo(() => {
    const list = normalizeInterventionsPayload(interventionsFromStore);
    if (
      currentInterventionFromStore?.id &&
      !list.some(
        (i) => String(i?.id) === String(currentInterventionFromStore.id)
      )
    ) {
      return [...list, currentInterventionFromStore];
    }
    return list;
  }, [interventionsFromStore, currentInterventionFromStore]);

  useEffect(() => {
    if (!patientId) return;
    dispatch(
      searchInterventions(patientId, {
        pageNumber: 1,
        pageSize: 100,
      })
    );
  }, [dispatch, patientId]);

  const isInterventionPendingForRow = useCallback(
    (med) =>
      isInterventionPendingForMedicationRow(
        med,
        interventionsList,
        pmrClinicalServiceId
      ),
    [interventionsList, pmrClinicalServiceId]
  );

  const safePatient = patient ?? {};
  const patientCaregiver = firstProfileEntry(safePatient.caregiver);
  const patientCareProvider = primaryCareProvider(safePatient.careProvider);
  const patientContactInfo = safePatient.contactInfo ?? {};
  // Callers build these context values inline, so an unset source yields a fresh
  // object/array on every render. Falling back to a shared constant keeps the
  // memo below from re-running forever.
  const safePatientMedications = Array.isArray(patientMedications) ? patientMedications : EMPTY_LIST;
  const safeMedicationRecords = Array.isArray(medications?.medications)
    ? medications.medications
    : Array.isArray(medications)
      ? medications
      : EMPTY_LIST;

  const { currentLoggedInUserDetail: userFromCurrentState } = useSelector((state) => state.currentLoggedInUserDetail ?? {});
  const usersState = useSelector((state) => state.users ?? {});
  const { currentLoggedInUserDetail: userFromUsers, pharmacyStores } = usersState;
  const currentLoggedInUserDetail = userFromCurrentState ?? userFromUsers;
  const pharmacy = getPharmacyInfo(currentLoggedInUserDetail, pharmacyStores) ?? {};
  const pharmacistName = [currentLoggedInUserDetail?.firstName, currentLoggedInUserDetail?.lastName].filter(Boolean).join(" ") || "";
  const pharmacistSignatureUrl =
    currentLoggedInUserDetail?.signature && typeof currentLoggedInUserDetail.signature === "string" && currentLoggedInUserDetail.signature.length > 0
      ? (currentLoggedInUserDetail.signature.startsWith("data:") ? currentLoggedInUserDetail.signature : `data:image/png;base64,${currentLoggedInUserDetail.signature}`)
      : null;
  const reportDate = formatFormDate(endDateTime || startDateTime);

  const formValues = {
    allergyInformation: {
      noKnownAllergies: !safePatient.allergyInfo?.allergies?.length,
      hasKnownAllergies: safePatient.allergyInfo?.allergies?.length > 0,
      allergies:
        safePatient.allergyInfo?.allergies
          ?.map?.((a) => a.allergyName)
          ?.join(", ") || "",
    },
  };
  const medicationList = useMemo(() => {
    const patientMedicationById = buildPatientMedicationLookupMap(safePatientMedications);

    // PMR rows come from assessment `medications.medications`; if empty in intervention/linked-service views, fall back to `patientMedications`.
    const recordSource =
      safeMedicationRecords.length > 0
        ? safeMedicationRecords
        : safePatientMedications;

    const cards = recordSource.map((med) => {
      const patientMedication = findPatientMedicationForRow(
        med,
        patientMedicationById,
        safePatientMedications
      );
      const linkedKey = med.id ?? med.medicationId;
      const medicationName =
        patientMedication?.medicationName ||
        med.medicationName ||
        med.displayName ||
        "";
      const dosage = patientMedication?.dosage || med.dosage || "";
      const form = patientMedication?.form || med.form || "";
      // Do not use route as frequency — it belongs in HOW I TAKE IT, not in strength/form line.
      const frequency = patientMedication?.frequency || med.frequency || "";

      const builtName = medicationName
        ? `${medicationName} [${dosage} ${form} ${frequency}]`.replace(/\s+/g, " ").trim()
        : "";
      // Assessment cards often store the full AMR line on `name` when profile merge failed earlier.
      const name = builtName || (typeof med.name === "string" && med.name.trim() ? med.name.trim() : "");

      return {
        id: linkedKey,
        medicationId: med.medicationId ?? med.MedicationId ?? null,
        name,
        cardName: medicationName
          ? `${medicationName} ${patientMedication?.route || med.route || ""} ${getFullLabelByValue(frequency)}`.trim()
          : "",
        reasonForMedication:
          patientMedication?.reasonForMedicationName ||
          med.reasonForMedication ||
          med.reasonForMedicationName,
        reasonForMedicationCode:
          patientMedication?.reasonsForMedicationGenCode || med.reasonsForMedicationGenCode,
        medicationType: patientMedication?.medicationType || med.medicationType,
        comment: patientMedication?.comments || med.comments,
        specialIntructions:
          patientMedication?.specialIntructions || med.specialIntructions,
        consumption: patientMedication?.adherence || med.adherence,
        safe: patientMedication?.safety || med.safety,
        technique: patientMedication?.properTechnique || med.properTechnique,
        working: patientMedication?.effectiveness || med.effectiveness,
        intervention: patientMedication?.intervention ?? med?.intervention,
        status: patientMedication?.status ?? med?.status,
        drugType: patientMedication ? drugTypes.Prescription : drugTypes.Additional,
        generalTime: patientMedication?.generalTime || med.generalTime || [],
        dispenseQuantity: patientMedication?.dispenseQuantity ?? med.dispenseQuantity ?? null,
        route: patientMedication?.route || med.route || "",
        frequency: patientMedication?.frequency || med.frequency || "",
      };
    });

    safePatientMedications.forEach((med) => {
      const index = cards.findIndex((o) => cardRowMatchesPatientMed(o, med));
      if (index > -1) {
        const freq = med.frequency || "";
        if (med.medicationName) {
          cards[index].name = `${med.medicationName} [${med.dosage} ${med.form} ${freq}]`.replace(/\s+/g, " ").trim();
          cards[index].cardName = `${med.medicationName} ${med.route || ""} ${getFullLabelByValue(freq)}`.trim();
        }
        cards[index].dispenseQuantity = med.dispenseQuantity;
        cards[index].route = med.route || "";
        cards[index].frequency = freq;
      }
    });

    return cards;
  }, [safeMedicationRecords, safePatientMedications]);

  const isSubmitted = documentation?.isServiceSubmitted === true;
  // "yes" => pharmacist confirmed no interventions; "no" => interventions exist
  const noInterventionConfirmation = documentation?.noInterventionConfirmation;

  // Any listed medication with an unresolved PMR-marked intervention (store or embedded).
  const derivedActive = useMemo(
    () => medicationList.some((med) => isInterventionPendingForRow(med)),
    [medicationList, isInterventionPendingForRow]
  );
  // Any listed medication tied to a PMR-marked intervention at all (resolved or not).
  const derivedHasPmrIntervention = useMemo(
    () =>
      medicationList.some((med) =>
        rowHasAnyPmrIntervention(med, interventionsList, pmrClinicalServiceId)
      ),
    [medicationList, interventionsList, pmrClinicalServiceId]
  );

  // `hasActiveInterventions` / `pmrIsFinal` come from context when the PMR is opened from an
  // intervention (status-driven). Elsewhere (med-review docs, medical summary) context leaves
  // them undefined and we derive them from the medication list, so the watermark/pill always
  // match the row highlight.
  const hasActiveInterventions = useMemo(() => {
    if (hasActiveInterventionsFromContext !== undefined) {
      return hasActiveInterventionsFromContext;
    }
    return derivedActive || (isSubmitted && noInterventionConfirmation === "no");
  }, [
    hasActiveInterventionsFromContext,
    derivedActive,
    isSubmitted,
    noInterventionConfirmation,
  ]);

  const showFinalVersionPill =
    !hasActiveInterventions &&
    (pmrIsFinal === true ||
      (pmrIsFinal == null && derivedHasPmrIntervention));

  const pillBaseClass =
    "rounded-[4px] px-[10px] py-[8px] text-center text-[18px] font-semibold text-teal-300";
  let reviewPill = null;
  if (hasActiveInterventions) {
    reviewPill = (
      <div className={`${pillBaseClass} bg-blue-200`}>
        IN REVIEW - Active Interventions
      </div>
    );
  } else if (showFinalVersionPill) {
    reviewPill = <div className={`${pillBaseClass} bg-teal-60`}>Final Version</div>;
  }

  const pmrServiceRow = useMemo(() => {
    if (pmrClinicalServiceId == null || pmrClinicalServiceId === "") return null;
    const list = clinicalServicesSmall?.medicationReviews;
    if (!Array.isArray(list)) return null;
    return list.find((m) => String(m.id) === String(pmrClinicalServiceId)) ?? null;
  }, [clinicalServicesSmall?.medicationReviews, pmrClinicalServiceId]);

  const showServiceCancelledBanner = useMemo(() => {
    if (!pmrServiceRow) return false;
    const s = String(pmrServiceRow.status || "").toLowerCase();
    return (
      s.includes("cancelled") ||
      s.includes("deleted") ||
      s.includes("deleted_service") ||
      s.includes("cancelled_service")
    );
  }, [pmrServiceRow]);

  const getConsumption = (val) => {
    switch (val) {
      case AdherenceTypes.AsPrescribed:
        return "As Prescribed";
      case AdherenceTypes.NotAsPrescribed:
        return "Not as Prescribed";
      case AdherenceTypes.Stopped:
        return "Stopped";
      default:
        break;
    }
  };

  return (
    <div className={`pmr-form mx-auto max-w-[1086px] min-h-[816px] border-gray-400 ${hasActiveInterventions ? "relative" : ""}`}>
      {/* Watermark when there are active interventions - do not give this copy to the patient.
          Screen shows one centred mark. Print/PDF export replaces this with a per-page
          watermark (see documentationForm handlePrint / handleSendEmail). */}
      {hasActiveInterventions && (
        <div
          className="pmr-screen-watermark pointer-events-none absolute inset-0 z-10 flex items-center justify-center whitespace-nowrap"
          aria-hidden
        >
          <span
            className="select-none font-medium leading-none"
            style={{
              fontSize: "160px",
              color: "#000000",
              opacity: 0.07,
              transform: "rotate(-33deg)",
            }}
          >
            Under Review
          </span>
        </div>
      )}
      {/* Header with logos */}
      <div className="w-full bg-white px-8 py-0">
        <div className="flex justify-between">
          {/* Left side - Ontario logo and ministry info */}
          <div className="flex items-center space-x-4">
            {/* Ontario Trillium Logo */}
            <div className="flex items-center">
              <img src={ontarioLeftLogo} width="160" height="40" />
            </div>

            {/* Ministry text */}
            <div className="ml-8 text-left">
              <div
                className="text-black font-bold text-xs leading-snug tracking-normal"
                style={{
                  fontFamily: "Inter",
                  fontSize: "12px",
                  lineHeight: "100%",
                }}
              >
                Ministry of Health
              </div>
              <div
                className="text-black font-bold text-xs leading-snug tracking-normal"
                style={{
                  fontFamily: "Inter",
                  fontSize: "12px",
                  lineHeight: "100%",
                }}
              >
                and Long-Term Care
              </div>
            </div>
          </div>

          {/* Center - Ontario Pharmacists Association logo */}      
          <div className="flex-1 flex items-center justify-center">
            <img src={ontarioPharmaLogo} alt="Ontario Pharmacists Association" className="inline-block h-8 w-auto" />
          </div>

          {/* Right side - MedsCheck logo and optional In Review label */}
          <div className="flex flex-col items-end justify-center pt-2">
            <img src={medsCheckLogo} width="196" height="32" alt="MedsCheck" />
          </div>
        </div>
      </div>

      <div className="-mt-3 bg-white pl-6 pr-6 pb-2">
        {/* Main Title */}
        <div className="flex items-end justify-between gap-3 pb-1">
          <div className="font-bold text-lg leading-snug tracking-normal">
            Personal Medication Record
          </div>
          <div className="flex shrink-0 flex-col items-end gap-2">
            {showServiceCancelledBanner && (
              <div className="rounded-md bg-gray-200 px-3 py-2 text-center text-base font-bold tracking-wide text-gray-800">
                Service Cancelled
              </div>
            )}
            {reviewPill}
          </div>
        </div>

        <div className=" mx-auto bg-white font-semibold text-[11px] leading-snug tracking-normal">
          {/* Header Section */}
          <div className="mb-0">
            <div className="flex">
              <div className="flex-1 border-t border-r border-black p-0.5">
                <div className="mb-0.5 pl-[5px] text-[10px]">MedsCheck Service Provided</div>
                <div className="min-h-[1.25rem] pt-1 bg-gray-10 pl-[5px]"></div>
              </div>
              <div className="flex-1 p-0.5 border-t border-black">
                <div className="mb-0.5 pl-[5px] text-[10px]">
                  Location (Pharmacy / Patient's Home)
                </div>
                <div className="min-h-[1.25rem] pt-1 bg-gray-10 pl-[5px]"></div>
              </div>
            </div>
          </div>

          {/* Patient Information Section */}
          <div className="mb-0">
            <div className="p-0.5 border-b border-t border-black ">
              <div className="p-0.5 bg-gray-20 pl-[5px] font-semibold text-[12px] leading-snug tracking-normal">
                Patient Information
              </div>
            </div>

            {/* Name and DOB Row */}
            <div className="flex border-b border-black">
              <div className="flex-1 border-r border-black p-0.5">
                <div className="mb-0.5 pl-[5px] text-[10px]">Patient Last Name</div>
                <div className="min-h-[1.25rem] pt-1 bg-gray-10 pl-[5px] text-[10px]">
                  {safePatient.lastName}
                </div>
              </div>
              <div className="flex-1 border-r border-black p-0.5">
                <div className="mb-0.5 pl-[5px] text-[10px]">Patient First Name</div>
                <div className="min-h-[1.25rem] pt-1 bg-gray-10 pl-[5px] text-[10px]">
                  {safePatient.firstName}
                </div>
              </div>
              <div className="w-48 p-0.5">
                <div className="mb-0.5 pl-[5px] text-[10px]">Date of Birth (yyyy/mm/dd)</div>
                <div className="min-h-[1.25rem] pt-1 bg-gray-10 pl-[5px] text-[10px]">
                  {formatFormDate(safePatient.dateOfBirth)}
                </div>
              </div>
            </div>

            {/* Address Row */}
            <div className="flex border-b border-black">
              <div className="w-[100px] border-r border-black p-0.5">
                <div className="mb-0.5 pl-[5px] text-[10px]">Unit Number</div>
                <div className="min-h-[1.25rem] pt-1 bg-gray-10 pl-[5px] text-[10px]">
                  {patientContactInfo.unitNumber}
                </div>
              </div>
              <div className="w-[100px] border-r border-black p-0.5">
                <div className="mb-0.5 pl-[5px] text-[10px]">Street Number</div>
                <div className="min-h-[1.25rem] pt-1 bg-gray-10 pl-[5px] text-[10px]">
                  {patientContactInfo.address?.split(" ")[0]}
                </div>
              </div>
              <div className="w-[273px] border-r border-black p-0.5">
                <div className="mb-0.5 pl-[5px] text-[10px]">Street Name</div>
                <div className="min-h-[1.25rem] pt-1 bg-gray-10 pl-[5px] text-[10px]">
                  {patientContactInfo.address?.split(" ").slice(1).join(" ")}
                </div>
              </div>
              <div className="w-[100px] border-r border-black p-0.5">
                <div className="mb-0.5 pl-[5px] text-[10px]">PO BOX</div>
                <div className="min-h-[1.25rem] pt-1 bg-gray-10 pl-[5px] text-[10px]">
                  {patientContactInfo.poBox}
                </div>
              </div>
              <div className="w-[220px] border-r border-black p-0.5">
                <div className="mb-0.5 pl-[5px] text-[10px]">City/Town</div>
                <div className="min-h-[1.25rem] pt-1 bg-gray-10 pl-[5px] text-[10px]">
                  {patientContactInfo.city}
                </div>
              </div>
              <div className="w-[140px] border-r border-black p-0.5">
                <div className="mb-0.5 pl-[5px] text-[10px]">Province</div>
                <div className="min-h-[1.25rem] pt-1 bg-gray-10 pl-[5px] text-[10px]">
                  {patientContactInfo.province}
                </div>
              </div>
              <div className="w-[105px] p-0.5">
                <div className="mb-0.5 pl-[5px] text-[10px]">Postal Code</div>
                <div className="h-5 pt-1 bg-gray-10 pl-[5px] text-[10px]">
                  {patientContactInfo.postalCode}
                </div>
              </div>
            </div>

            {/* Contact Info Row */}
            <div className="flex border-b border-black">
              <div className="w-40 border-r border-black p-0.5">
                <div className="mb-0.5 pl-[5px] text-[10px]">Telephone Number</div>
                  <div className="min-h-[1.25rem] pt-1 bg-gray-10 pl-[5px] text-[10px]">
                  {patientContactInfo.primaryPhone}
                </div>
              </div>
              <div className="w-48 border-r border-black p-0.5">
                <div className="mb-0.5 pl-[5px] text-[10px]">
                  Date of Interview (yyyy/mm/dd)
                </div>
                <div className="min-h-[1.25rem] pt-1 bg-gray-10 pl-[5px]"></div>
              </div>
              <div className="flex-1 p-0.5">
                <div className="mb-0.5 pl-[5px] text-[10px]">Email Address</div>
                <div className="min-h-[1.25rem] pt-1 bg-gray-10 pl-[5px] text-[10px]">
                  {patientContactInfo.email}
                </div>
              </div>
            </div>

            {/* Sources Caregiver Row */}
            <div className="">
              <div className="p-0.5 ">
                <div className="p-0.5 pl-[5px] text-[10px]">
                  Sources Caregiver and/or Contact Name
                </div>
              </div>
              <div className="flex">
                <div className="flex-1 border-r border-black p-0.5">
                  <div className="mb-0.5 pl-[5px] text-[10px]">Last Name</div>
                  <div className="min-h-[1.25rem] pt-1 bg-gray-10 pl-[5px] text-[10px]">
                    {patientCaregiver.lastName}
                  </div>
                </div>
                <div className="flex-1 border-r border-black p-0.5">
                  <div className="mb-0.5 pl-[5px] text-[10px]">First Name</div>
                  <div className="min-h-[1.25rem] pt-1 bg-gray-10 pl-[5px] text-[10px]">
                    {patientCaregiver.firstName}
                  </div>
                </div>
                <div className="w-40 p-0.5">
                  <div className="mb-0.5 pl-[5px] text-[10px]">Telephone Number</div>
                  <div className="min-h-[1.25rem] pt-1 bg-gray-10 pl-[5px] text-[10px]">
                    {patientCaregiver.contactInfo?.primaryPhone}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Primary Care Provider Section */}
          <div className="border-t-0 border-black">
            <div className="p-0.5 border-b border-t border-black ">
              <div className="p-1 bg-gray-20 pl-[5px] font-semibold text-[13px] leading-snug tracking-normal">
                Primary Care Provider
              </div>
            </div>
            <div className="flex">
              <div className="flex-1 border-r border-black p-0.5">
                <div className="mb-1 pl-[5px]">Last Name</div>
                <div className="h-6 pt-2 bg-gray-10 pl-[5px]">
                  {patientCareProvider.lastName}
                </div>
              </div>
              <div className="flex-1 border-r border-black p-0.5">
                <div className="mb-1 pl-[5px]">First Name</div>
                <div className="h-6 pt-2 bg-gray-10 pl-[5px]">
                  {patientCareProvider.firstName}
                </div>
              </div>
              <div className="w-40 border-r border-black p-0.5">
                <div className="mb-1 pl-[5px]">Telephone Number</div>
                <div className="h-6 pt-2 bg-gray-10 pl-[5px]">
                  {patientCareProvider.contactInfo?.primaryPhone}
                </div>
              </div>
              <div className="w-32 p-0.5">
                <div className="mb-1 pl-[5px]">Fax Number</div>
                <div className="h-6 pt-2 bg-gray-10 pl-[5px]">
                  {patientCareProvider.contactInfo?.fax}
                </div>
              </div>
            </div>
          </div>

          <div className="border-t-0 border-black">
            <div className="p-0.5 border-b border-t border-black ">
              <div className="p-0.5 bg-gray-20 pl-[5px] font-semibold text-[12px] leading-snug tracking-normal">
                Current Medication List - Prescription, Non-Prescription,
                Natural Health Products
              </div>
            </div>

            <div className="flex">
              <div className="flex-1 p-0.5">
                <div className="mb-0.5 pl-[5px] text-[10px]">
                  Known Allergies and/or Intolerances
                </div>
                <div className="min-h-[1.25rem] pt-1 bg-gray-10 pl-[5px] text-[10px]">
                  {formValues.allergyInformation.allergies}
                </div>
              </div>
            </div>
            <div className="flex border-t border-black p-0.5">
              <div className="flex">
                <input type="checkbox" className="mr-2" />
                <span className="mt-1">
                  Select only if patient is not taking any non-prescription
                  products (ie/ vitamins, health products, over-the-counter)
                </span>
              </div>
            </div>
          </div>

          <div className="w-full overflow-x-auto pt-1">
            <table className="w-full border-collapse table-fixed">
              <thead>
                <tr>
                  <th className="border-t border-r border-black text-center bg-white pt-1 pb-1 text-[10px] w-[336px]">
                    <p>WHAT I TAKE</p>
                    <p>
                      Name, strength & form of medication as noted on the
                      prescription or medication package label
                    </p>
                  </th>
                  <th className="border-t border-r border-black text-center bg-white pt-1 pb-1 text-[10px] w-[210px]">
                    <p>WHY I TAKE IT</p>
                    <p>Disease, condition or symptoms it addresses</p>
                  </th>
                  <th className="border-t border-r  border-black text-center font-bold bg-white pt-1 pb-1 text-[10px] w-[241px]">
                    <p>HOW I TAKE IT</p>
                    <p>Qty, route, times per day or certain time of day</p>
                  </th>
                  <th className="border-t  border-black text-center font-bold bg-white pt-1 pb-1 text-[10px] w-[246px]">
                    <p>COMMENTS</p>
                    <p>
                      eg. Special Instructions; drug - related issues
                      identified; prescriber (if different); etc.
                    </p>
                  </th>
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: Math.max(4, medicationList.length) }, (_, i) => {
                  const x = medicationList[i];
                  const hasPendingIntervention = x && isInterventionPendingForRow(x);
                  const rowBackgroundClass = hasPendingIntervention ? "bg-yellow-60" : "bg-gray-10";

                  return (
                    <tr key={x ? `med-${x.id}` : `empty-${i}`} className={hasPendingIntervention ? "bg-yellow-60" : ""}>
                      <td className={`border-t border-r border-black p-1 min-h-[16px] align-top ${rowBackgroundClass} text-[10px]`}>
                        {hasPendingIntervention && x?.name ? (
                          <span className="inline-flex items-start gap-1">
                            <img
                              src="/icons/intervention-sm.svg"
                              alt="Intervention pending"
                              aria-label="Intervention pending"
                              className="h-3 w-3 shrink-0 mt-[1px]"
                            />
                            <span>{x.name}</span>
                          </span>
                        ) : (
                          x?.name ?? ""
                        )}
                      </td>
                      <td className={`border-t border-r border-black p-1 min-h-[16px] align-top ${rowBackgroundClass} text-[10px]`}>
                        {x ? GetDropDownValue(reasonOptions, x.reasonForMedication) : ""}
                      </td>
                      <td className={`border-t border-r border-black p-1 min-h-[16px] align-top ${rowBackgroundClass} text-[10px]`}>
                        {x ? formatHowITakeIt(x) : ""}
                      </td>
                      <td className={`border-t border-black p-1 min-h-[16px] align-top ${rowBackgroundClass} text-[10px]`}>
                        {x ? (
                          <>
                            {getConsumption(x.consumption)}
                            {x.specialIntructions != null && x.specialIntructions ? ` ${x.specialIntructions}` : ""}
                          </>
                        ) : (
                          ""
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="border-t border-black">
            <div className="flex pt-1">
              <div className="flex items-start">
                <span className="text-[10px] font-medium mb-0.5">
                  Attention Health Care Professionals: A more detailed version
                  of this MedsCheck Review that includes professional notes is
                  available from the pharmacy named. Sources of information in
                  this document include (but are not limited to) local pharmacy
                  data and the patient. The patient has been informed of the
                  intent of the MedsCheck Review and on what to expect. The
                  patient is responsible for the accuracy and completeness of
                  the data they provided when this document was prepared and for
                  advising the pharmacist of any change to these medications.
                  The pharmacist is responsible for information in this document
                  that changed as a result of providing a medication review
                  service to the patient.
                </span>
              </div>
            </div>
          </div>

          <div className="w-full bg-white font-semibold text-[11px] leading-snug tracking-normal">
            {/* Main form container - pharmacy/user info when present */}
            <div className="border border-black flex">
              {/* Left section - Pharmacy Name (spans full height) */}
              <div className="w-[38%] border-r border-black p-0.5 bg-gray-20">
                <div className="mb-0.5 text-[10px]">
                  Pharmacy Name (and/or Logo/Label) and Address
                </div>
                <div className="min-h-[40px] text-[10px] whitespace-pre-line">
                  {[pharmacy.pharmacyName, pharmacy.pharmacyStoreNumber].filter(Boolean).join(" ")}
                  {pharmacy.pharmacyAddress ? `\n${pharmacy.pharmacyAddress}` : ""}
                </div>
              </div>

              {/* Right section - divided into rows and columns */}
              <div className="w-[62%] flex flex-col">
                {/* Top row of right section */}
                <div className="flex border-b border-black">
                  {/* MedsCheck Pharmacist Name */}
                  <div className="flex-1 border-r border-black p-0.5">
                    <div className="mb-0.5 text-[10px]">MedsCheck Pharmacist Name</div>
                    <div className="min-h-[14px] text-[10px] bg-gray-10">{pharmacistName}</div>
                  </div>

                  {/* Pharmacy Telephone Number */}
                  <div className="w-[40%] p-0.5">
                    <div className="mb-0.5 text-[10px]">Pharmacy Telephone Number</div>
                    <div className="min-h-[14px] text-[10px] bg-gray-10">{pharmacy.pharmacyPhone || ""}</div>
                  </div>
                </div>

                {/* Bottom row of right section */}
                <div className="flex">
                  {/* Pharmacist's Signature */}
                  <div className="flex-1 border-r border-black p-0.5">
                    <div className="mb-0.5 text-[10px]">Pharmacist's Signature</div>
                    <div className="min-h-[14px] flex items-center">
                      {pharmacistSignatureUrl ? (
                        <img src={pharmacistSignatureUrl} alt="" className="max-h-5 w-auto object-contain" />
                      ) : null}
                    </div>
                  </div>

                  {/* Date MedsCheck Report Completed */}
                  <div className="flex-1 border-r border-black p-0.5">
                    <div className="mb-0.5 text-[10px]">
                      Date MedsCheck Report Completed (yyyy/mm/dd)
                    </div>
                    <div className="min-h-[14px] text-[10px]">{reportDate}</div>
                  </div>

                  {/* Pharmacy Fax Number */}
                  <div className="w-[\20%] p-0.5">
                    <div className="mb-0.5 text-[10px]">Pharmacy Fax Number</div>
                    <div className="min-h-[14px] text-[10px]">{pharmacy.pharmacyFax || ""}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Static footer line with form code and page info */}
          <div className="mt-1 flex w-full text-[10px] text-gray-700">
            <span className="flex-1">4968E (2022/11) &nbsp; &nbsp; © King&apos;s Printer for Ontario, 2022</span>
            <span className="flex-1 text-center"> Disponible en français</span>
            <span className="flex-1 text-right">Page 1 of 1</span>
          </div>
        </div>
      </div>
    </div>
  );
};
